import { useEffect, useMemo } from 'react';
import { Plus, Star, X } from 'lucide-react';
import { Icon } from '@/core/ui/icons';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useFilterStore } from '../store/filterStore';
import { filterTasks, uniqueAssignees, uniqueTags } from '@/features/tasks/filter';
import { useToastStore } from '@/core/ui/toastStore';
import { useState } from 'react';
import type { GroupBy, SortBy } from '@/features/tasks/grouping';

/** Named filter set, stored per project in the browser. */
interface SavedView {
  name: string;
  view?: string;
  groupBy: GroupBy;
  sortBy: SortBy;
  assigneeId: number | null;
  tagId: string | null;
  onlyUnassigned: boolean;
  dueThisWeek: boolean;
  showCompleted?: boolean;
}
function loadViews(areaId: number): SavedView[] {
  try { return JSON.parse(localStorage.getItem('cgp:area:' + areaId + ':views') ?? '[]') as SavedView[]; } catch { return []; }
}
function storeViews(areaId: number, views: SavedView[]): void {
  try { localStorage.setItem('cgp:area:' + areaId + ':views', JSON.stringify(views)); } catch { /* best-effort */ }
}

const GROUPS: { value: GroupBy; label: string }[] = [
  { value: 'stage', label: 'Stage' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'priority', label: 'Priority' },
  { value: 'due', label: 'Due date' },
];
const SORTS: { value: SortBy; label: string }[] = [
  { value: 'priority', label: 'Priority' },
  { value: 'due', label: 'Due date' },
  { value: 'name', label: 'Name' },
  { value: 'manual', label: 'Manual' },
];

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-md border px-2 py-1 text-xs font-medium transition ${
        active ? 'border-brand bg-brand/15 text-brand' : 'border-line text-muted hover:bg-hover hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

export function FilterBar() {
  const area = useAreaStore((s) => s.areas.find((a) => a.id === s.selectedAreaId) ?? null);
  const areaId = area?.id ?? null;
  const tasks = useTasksStore((s) => (areaId ? s.tasksByArea[areaId] : undefined));
  const loadTasks = useTasksStore((s) => s.loadTasks);
  const myUid = useAuthStore((s) => s.user?.uid ?? null);

  const f = useFilterStore();
  const areaStages = useAreaStore((s) => (s.selectedAreaId ? s.stagesByArea[s.selectedAreaId] : undefined));
  const setView = useAreaStore((s) => s.setView);
  const currentView = useAreaStore((s) => s.view);
  const toast = useToastStore((s) => s.show);
  // Memoise so `loaded` is stable across renders where tasks is null — otherwise
  // `[] !== []` re-runs the assignee/tag memos every render (exhaustive-deps).
  const loaded = useMemo(() => tasks ?? [], [tasks]);
  const dateField = area?.calendarDateField ?? 'date_deadline';
  const assignees = useMemo(() => uniqueAssignees(loaded), [loaded]);
  const tags = useMemo(() => uniqueTags(loaded), [loaded]);
  const [viewsTick, setViewsTick] = useState(0); // bump to re-read localStorage
  // position:fixed — the filter bar scrolls horizontally, and overflow-x:auto
  // CLIPS absolutely-positioned children (the dropdown would be invisible).
  const [viewsMenu, setViewsMenu] = useState<{ top: number; left: number } | null>(null);
  const savedViews = useMemo(() => (areaId !== null ? loadViews(areaId) : []), [areaId, viewsTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const visibleCount = useMemo(
    () => filterTasks(loaded, { search: f.search, assigneeId: f.assigneeId, tagId: f.tagId, stageId: f.stageId, onlyUnassigned: f.onlyUnassigned, dueThisWeek: f.dueThisWeek, dateField }).length,
    [loaded, f.search, f.assigneeId, f.tagId, f.stageId, f.onlyUnassigned, f.dueThisWeek, dateField],
  );

  // A board can declare its cards' shoot + delivery dates past (Area.hideKeyDates
  // — Content Calendar). "Due this week" is then a trap: it would hide most of the
  // board on a date nothing on screen shows. The chip is gone below; clear the
  // FILTER too, so a saved view carrying it can't leave the board quietly pruned.
  const hideDates = Boolean(area?.hideKeyDates);
  const setDueThisWeek = f.setDueThisWeek;
  const dueOn = f.dueThisWeek;
  useEffect(() => {
    if (hideDates && dueOn) setDueThisWeek(false);
  }, [hideDates, dueOn, setDueThisWeek]);

  if (areaId === null) return null;
  const active = Boolean(f.search || f.assigneeId || f.tagId || f.stageId || f.onlyUnassigned || f.dueThisWeek);

  const saveCurrentView = () => {
    const name = window.prompt('Name this view (e.g. "My overdue", "Design queue"):');
    if (!name?.trim()) return;
    const next: SavedView = {
      name: name.trim().slice(0, 40),
      view: currentView,
      groupBy: f.groupBy, sortBy: f.sortBy,
      assigneeId: f.assigneeId, tagId: f.tagId,
      onlyUnassigned: f.onlyUnassigned, dueThisWeek: f.dueThisWeek,
      showCompleted: f.showCompleted,
    };
    const views = loadViews(areaId).filter((v) => v.name !== next.name);
    storeViews(areaId, [...views, next]);
    setViewsTick((t) => t + 1);
    toast('View "' + next.name + '" saved — pick it from Views any time.', 'info');
  };
  const applyView = (v: SavedView) => {
    // Skip filters that can no longer match anything visible — an invisible
    // active filter reads as "the board is empty and I don't know why".
    const tagOk = !v.tagId || tags.some((t) => t.key === v.tagId);
    const personOk = !v.assigneeId || v.assigneeId === myUid || assignees.some((a) => a.id === v.assigneeId);
    // Set the whole filter state ATOMICALLY — the individual setters mutually
    // clear assigneeId ⇄ onlyUnassigned, so a sequence of them would wipe a
    // saved "Unassigned" (or assignee) view. One setState avoids that.
    useFilterStore.setState({
      groupBy: v.groupBy,
      sortBy: v.sortBy,
      // A view is a COMPLETE filter statement — a leftover stage focus would
      // silently intersect it (the invisible-filter empty-board trap).
      stageId: null,
      tagId: tagOk ? v.tagId : null,
      dueThisWeek: v.dueThisWeek,
      onlyUnassigned: v.onlyUnassigned,
      assigneeId: v.onlyUnassigned ? null : (personOk ? v.assigneeId : null),
      // showCompleted changes what's FETCHED — a 'Done this month' view opens
      // empty without it. Default false for older saved views.
      showCompleted: v.showCompleted ?? false,
    });
    if ((!tagOk && v.tagId) || (!personOk && v.assigneeId)) toast('Part of that view no longer matches (tag or person gone) — applied the rest.', 'info');
    if (v.view === 'board' || v.view === 'table' || v.view === 'calendar') setView(v.view);
  };
  const deleteView = (name: string) => {
    storeViews(areaId, loadViews(areaId).filter((v) => v.name !== name));
    setViewsTick((t) => t + 1);
  };

  const toggleCompleted = () => {
    const next = !f.showCompleted;
    f.setShowCompleted(next);
    void loadTasks(areaId, next);
  };
  const toggleMine = () => f.setAssignee(f.assigneeId === myUid ? null : myUid);

  return (
    <div className="flex h-11 shrink-0 items-center gap-2 overflow-x-auto border-b border-line px-4 scrollbar-hide">
      <div className="relative shrink-0">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-faint">⌕</span>
        {/* id: the global '/' shortcut (Layout keymap) focuses this by id. */}
        <input
          id="cgp-filter-search"
          value={f.search}
          onChange={(e) => f.setSearch(e.target.value)}
          placeholder="Search…"
          className="h-7 w-44 rounded-md border border-line bg-canvas pl-6 pr-2 text-xs text-ink outline-none placeholder:text-faint focus:border-brand"
        />
      </div>

      <label className="flex shrink-0 items-center gap-1 text-xs text-faint">
        Group
        <select
          value={f.groupBy}
          onChange={(e) => f.setGroupBy(e.target.value as GroupBy)}
          className="h-7 rounded-md border border-line bg-canvas px-1.5 text-xs text-ink outline-none focus:border-brand"
        >
          {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
        </select>
      </label>

      <label className="flex shrink-0 items-center gap-1 text-xs text-faint">
        Sort
        <select
          value={f.sortBy}
          onChange={(e) => f.setSortBy(e.target.value as SortBy)}
          className="h-7 rounded-md border border-line bg-canvas px-1.5 text-xs text-ink outline-none focus:border-brand"
        >
          {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </label>

      <span className="mx-0.5 h-5 w-px shrink-0 bg-line" />

      {myUid && <Chip active={f.assigneeId === myUid} onClick={toggleMine}>Mine</Chip>}
      <Chip active={f.onlyUnassigned} onClick={() => f.setOnlyUnassigned(!f.onlyUnassigned)}>Unassigned</Chip>
      {!hideDates && <Chip active={f.dueThisWeek} onClick={() => f.setDueThisWeek(!f.dueThisWeek)}>Due this week</Chip>}

      {/* "Proper filtering" (Allyson, 8/10): pick a stage here, or click a
          column's title on the board — same filter, and it carries into the
          Table and Calendar views too. */}
      {(areaStages?.length ?? 0) > 0 && (
        <select
          value={f.stageId ?? ''}
          onChange={(e) => f.setStage(e.target.value ? Number(e.target.value) : null)}
          className={'h-7 max-w-[170px] shrink-0 rounded-md border px-1.5 text-xs outline-none focus:border-brand ' + (f.stageId ? 'border-brand bg-brand/10 text-brand' : 'border-line bg-canvas text-ink')}
          title="Show only one column"
        >
          <option value="">Any stage</option>
          {(areaStages ?? []).map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
        </select>
      )}

      <select
        value={f.assigneeId && f.assigneeId !== myUid ? f.assigneeId : ''}
        onChange={(e) => f.setAssignee(e.target.value ? Number(e.target.value) : null)}
        className="h-7 max-w-[150px] shrink-0 rounded-md border border-line bg-canvas px-1.5 text-xs text-ink outline-none focus:border-brand"
      >
        <option value="">Anyone</option>
        {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>

      {tags.length > 0 && (
        <select
          value={f.tagId ?? ''}
          onChange={(e) => f.setTag(e.target.value || null)}
          className={'h-7 max-w-[150px] shrink-0 rounded-md border px-1.5 text-xs outline-none focus:border-brand ' + (f.tagId ? 'border-brand bg-brand/10 text-brand' : 'border-line bg-canvas text-ink')}
          title="Filter by tag"
        >
          <option value="">Any tag</option>
          {tags.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
        </select>
      )}

      <span className="mx-0.5 h-5 w-px shrink-0 bg-line" />

      <label className="flex shrink-0 cursor-pointer select-none items-center gap-1.5 rounded-md border border-line px-2 py-1 text-xs text-muted hover:bg-hover">
        <input type="checkbox" checked={f.showCompleted} onChange={toggleCompleted} className="accent-brand" />
        Completed
      </label>

      {active && (
        <button onClick={f.reset} className="shrink-0 rounded-md px-2 py-1 text-xs text-muted hover:bg-hover hover:text-ink">
          Clear
        </button>
      )}

      <span className="mx-0.5 h-5 w-px shrink-0 bg-line" />

      {/* Saved views — a named shortcut back to a filter setup you use daily.
          Click-toggled (never hover-gated — phones exist). */}
      <div className="shrink-0">
        <button
          onClick={(e) => {
            if (viewsMenu) { setViewsMenu(null); return; }
            const r = e.currentTarget.getBoundingClientRect();
            setViewsMenu({ top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - 240) });
          }}
          className={'flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition ' + (viewsMenu ? 'border-brand bg-brand/10 text-brand' : savedViews.length ? 'border-line text-muted hover:bg-hover hover:text-ink' : 'border-line text-faint hover:bg-hover hover:text-muted')}
        >
          <Icon i={Star} size={14} /> Views{savedViews.length ? ' · ' + savedViews.length : ''}
        </button>
        {viewsMenu && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setViewsMenu(null)} />
            <div style={{ position: 'fixed', top: viewsMenu.top, left: viewsMenu.left }} className="z-30 w-56 overflow-hidden rounded-md border border-line bg-canvas shadow-xl">
              {savedViews.map((v) => (
                <div key={v.name} className="flex items-center border-b border-line last:border-0">
                  <button onClick={() => { applyView(v); setViewsMenu(null); }} className="min-w-0 flex-1 truncate px-3 py-2 text-left text-[12px] text-ink hover:bg-hover">{v.name}</button>
                  <button onClick={() => deleteView(v.name)} className="px-2 text-faint hover:text-danger" title="Delete view"><Icon i={X} size={14} /></button>
                </div>
              ))}
              <button onClick={() => { saveCurrentView(); setViewsMenu(null); }} className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[12px] text-muted hover:bg-hover hover:text-ink"><Icon i={Plus} size={14} /> Save current as view…</button>
            </div>
          </>
        )}
      </div>

      <div className="ml-auto shrink-0 whitespace-nowrap pl-2 text-xs text-faint">
        {active ? `${visibleCount} of ${loaded.length}` : `${loaded.length}`} {f.showCompleted ? 'tasks' : 'open'}
      </div>
    </div>
  );
}
